import * as React from 'react';

import { emptyObject } from 'bones/constants/empty';
import { XSD_ANY_URI } from 'bones/constants/iris';
import { MimeType } from 'bones/constants/mimeTypes';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { Attribute, AttributeItem } from 'bones/constants/voicebox/panel';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { useWithLoading } from 'bones/utils/hooks/useWithLoading';
import { makeStardogQueryName } from 'bones/utils/voicebox/stardogQuery';
import {
  QueryArgs,
  ResultBindings,
  usePortalVoiceboxStardogQuery,
} from 'bones/utils/voicebox/usePortalVoiceboxStardogQuery';

const getAttributeItemsQueryArgs = (
  { iri, predicateIri }: { iri: string; predicateIri: string },
  querySchema: boolean,
  limit: number,
  offset: number
): QueryArgs<
  ResultBindings<{
    attribute: string;
  }>,
  AttributeItem[]
> => ({
  query: `\
# ${makeStardogQueryName(
    'Voicebox Attribute Items',
    `${iri} ${predicateIri} (${limit}, ${offset})`
  )}
SELECT DISTINCT
  ?attribute
WHERE {
  <${iri}> <${predicateIri}> ?attribute .
  FILTER(isLiteral(?attribute))
}
LIMIT ${limit}
OFFSET ${offset}
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  querySchema,
  parseBody: async (body) => {
    const bindings = body?.results?.bindings || [];
    return bindings.reduce<AttributeItem[]>((acc, binding) => {
      if (binding.attribute?.value && binding.attribute.type === 'literal') {
        const type =
          binding.attribute.datatype === XSD_ANY_URI &&
          /^https?:\/\//i.test(binding.attribute.value)
            ? 'mediaLiteral'
            : binding.attribute.type;

        acc.push({
          type,
          value: binding.attribute.value,
        });
      }
      return acc;
    }, []);
  },
});

export const usePortalVoiceboxAttributeItemsFetcher = (
  connectionIdx: number,
  limit: number,
  settingsContext: VoiceboxSettingsContextValue,
  setAttributeItems: (
    { iri, predicateIri }: { iri: string; predicateIri: string },
    action: React.SetStateAction<Attribute>
  ) => void
) => {
  const { modelsByDatabaseId, settings } = settingsContext;
  const modelsById = modelsByDatabaseId[settings.databaseId] || emptyObject;
  const { classesByIri } = modelsById[settings.model || DEFAULT_MODEL_NAME] || {
    classesByIri: emptyObject,
  };

  const isMounted = useIsMounted();
  const withLoading = useWithLoading('usePortalVoiceboxAttributeItemsFetcher');

  const fetchAttributeItems = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getAttributeItemsQueryArgs
  );

  const fetchWithOffset = React.useCallback(
    async (
      { iri, predicateIri }: { iri: string; predicateIri: string },
      offset: number
    ) => {
      const isInstance = Boolean(!classesByIri[iri]);

      return withLoading(
        (loading) =>
          setAttributeItems({ iri, predicateIri }, (prev) => ({
            ...prev,
            ...(offset ? { loadingMore: loading } : { loading }),
          })),
        async () => {
          const newItems = await fetchAttributeItems(
            { iri, predicateIri },
            !isInstance,
            limit,
            offset
          );
          if (!newItems || !isMounted.current) {
            return;
          }

          setAttributeItems({ iri, predicateIri }, (prev) => {
            const items = [...(offset ? prev.items : []), ...newItems];
            const hasMore = prev.count > items.length;
            return {
              ...prev,
              items,

              hasMore,
              loadMore: async () => {
                if (hasMore) {
                  await fetchWithOffset({ iri, predicateIri }, items.length);
                }
              },
            };
          });
        }
      );
    },
    [
      classesByIri,
      fetchAttributeItems,
      isMounted,
      limit,
      setAttributeItems,
      withLoading,
    ]
  );

  return fetchWithOffset;
};
